import { useState, useEffect } from 'react';
import { createContext } from 'use-context-selector';
import type { FC } from 'react';
import { SETTING_KEYS, THEMES } from '../constants';
import { Logger } from 'src/utils/logger';
import { ContextProviderProps } from 'src/types/contextProviderProps';
import { Settings } from 'src/types/settings';
import useAuth from 'src/hooks/useAuth';
import { UserSetting } from 'src/types/userSetting';
import { gridColumnProperties } from 'src/types/gridColumnProperties';
import SettingsApi from 'src/api/SettingsApi';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getStrippedDownGridColumnArray, mergeColumnsIntoDefaultColumns } from 'src/utils/settingsHelper';

export interface SettingsContextValue {
  saveSettings: (update: Settings) => void;
  saveGridSettings: (update: gridColumnProperties[], key: string) => void;
  savePagingSettings: (update: number, key: string) => void;
  resetGridSettings: () => void;
  getColumnsWithSettings: (localStorageKey: string, defaultColumns: gridColumnProperties[]) => gridColumnProperties[];
  saveColumnsToLocalStorage: (cols: gridColumnProperties[], localStorageKey: string) => void;
  saveShowOrderSummaryWidget?: (show: boolean) => void;
  userSettings: UserSetting[];
  settings: Settings;
  settingsHaveLoaded: boolean;
}

const initialSettings: Settings = {
  contentWidth: 'standard',
  direction: 'ltr',
  responsiveFontSizes: true,
  roundedCorners: true,
  showOrderSummaryWidget: true,
  theme:
    typeof window !== 'undefined' && localStorage.getItem('theme')
      ? localStorage.getItem('theme')
      : window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
      ? THEMES.FOREST
      : THEMES.LIGHT,
  notificationDays: 5,
  notificationModalEnabled: true,
};

const initialUserSettings = (screenSize: boolean): UserSetting[] => [{ settingKey: SETTING_KEYS.Settings, settingValue: { ...initialSettings }, smallScreen: screenSize }];

export const restoreSettings = async (smallScreen: boolean): Promise<UserSetting[] | null> => {
  try {
    const allSettings = await SettingsApi.getUserSettings(smallScreen);

    let userSettings = allSettings.find((setting) => setting.settingKey === SETTING_KEYS.Settings)?.settingValue as Settings;

    if (!userSettings) {
      userSettings = initialSettings;
      allSettings.push({ settingKey: SETTING_KEYS.Settings, settingValue: userSettings, smallScreen: smallScreen });
    } else {
      const savedTheme = localStorage.getItem('theme') || initialSettings.theme;
      userSettings = { ...initialSettings, ...userSettings, theme: savedTheme };
    }

    return allSettings.map((setting) => (setting.settingKey === SETTING_KEYS.Settings ? { ...setting, settingValue: userSettings } : setting));
  } catch (err) {
    Logger.error(err);
    // returning an empty array resets the grid
    // defaults to be used so return null instead
    return null;
  }
};

export const storeSettings = (settings: UserSetting[]): void => {
  SettingsApi.setUserSettings(settings);
};

const SettingsContext = createContext<SettingsContextValue>({
  saveSettings: () => {},
  userSettings: [],
  saveGridSettings: () => {},
  savePagingSettings: () => {},
  resetGridSettings: () => {},
  getColumnsWithSettings: () => [],
  saveColumnsToLocalStorage: () => {},
  saveShowOrderSummaryWidget: () => {},
  settings: initialSettings,
  settingsHaveLoaded: false,
});

export const SettingsProvider: FC<ContextProviderProps> = (props) => {
  const { children } = props;
  const theme = useTheme();
  const isMobileDevice = useMediaQuery(theme.breakpoints.down('sm'));
  const [userSettings, setUserSettings] = useState<UserSetting[]>(initialUserSettings(isMobileDevice));
  const [settings, setSettings] = useState<Settings>(initialSettings);
  const [settingsHaveLoaded, setSettingsHaveLoaded] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    const fetchSettings = async () => {
      try {
        const restoredSettings = await restoreSettings(isMobileDevice);
        if (restoredSettings) {
          setUserSettings(restoredSettings);
          let basicSettings = restoredSettings.find((setting) => setting.settingKey === SETTING_KEYS.Settings);
          const settingsValue = basicSettings?.settingValue as Settings;
          setSettings(settingsValue);
          setSettingsHaveLoaded(true);
        }
      } catch (error) {
        console.error('Error fetching settings', error);
        Logger.error(error);
      }
    };

    fetchSettings();
  }, [isMobileDevice, user]);

  const saveSettings = async (updatedSettings: Settings): Promise<void> => {
    const newSettings = getUpdatedUserSettings({
      settingKey: SETTING_KEYS.Settings,
      smallScreen: isMobileDevice,
      settingValue: updatedSettings,
    });
    setUserSettings(newSettings);
    setSettings(updatedSettings);
    storeSettings(newSettings);
    localStorage.setItem('theme', updatedSettings.theme);
  };

  const resetGridSettings = async (): Promise<void> => {
    const deletedSettings = [...userSettings].filter((setting) => setting.settingKey !== SETTING_KEYS.Settings);
    SettingsApi.deleteUserSettings(deletedSettings);
    let basicSettings = userSettings.find((setting) => setting.settingKey === SETTING_KEYS.Settings);
    setUserSettings([basicSettings]);
  };

  const saveGridSettings = (updatedSettings: gridColumnProperties[], key: string): void => {
    const newSettings = getUpdatedUserSettings({
      settingKey: key,
      smallScreen: isMobileDevice,
      settingValue: getStrippedDownGridColumnArray(updatedSettings),
    });
    setUserSettings(newSettings);
    storeSettings(newSettings);
  };

  const savePagingSettings = (updatedSettings: number, key: string): void => {
    const newSettings = getUpdatedUserSettings({
      settingKey: key,
      smallScreen: isMobileDevice,
      settingValue: updatedSettings,
    });
    setUserSettings(newSettings);
    storeSettings(newSettings);
  };

  const getUpdatedUserSettings = (updatedSetting: UserSetting): UserSetting[] => {
    const newSettings = [...userSettings];
    const index = newSettings.findIndex((setting) => setting.settingKey === updatedSetting.settingKey);
    if (index > -1) {
      newSettings[index].settingValue = updatedSetting.settingValue;
    } else {
      newSettings.push(updatedSetting);
    }
    return newSettings;
  };

  const getColumnsWithSettings = (localStorageKey: string, defaultColumns: gridColumnProperties[]): gridColumnProperties[] => {
    let cols: gridColumnProperties[] = [];
    let gridSettings = userSettings.find((setting) => setting.settingKey === localStorageKey);

    if (gridSettings) {
      cols = gridSettings.settingValue as gridColumnProperties[];
      cols = getStrippedDownGridColumnArray(cols);
      let savedColsLength = cols.length;
      cols = mergeColumnsIntoDefaultColumns(cols, defaultColumns);

      if (defaultColumns.length > savedColsLength) {
        for (let i = savedColsLength - 1; i < defaultColumns.length; i++) {
          cols[i].orderIndex = i;
        }
      }
    } else {
      cols = [...defaultColumns];
    }

    return cols;
  };

  const saveColumnsToLocalStorage = (cols: gridColumnProperties[], localStorageKey: string): void => {
    let strippedDownColData = getStrippedDownGridColumnArray(cols);
    localStorage.setItem(localStorageKey, JSON.stringify(strippedDownColData));
  };

  const saveShowOrderSummaryWidget = async (show: boolean): Promise<void> => {
    const updatedSettings = { ...settings, showOrderSummaryWidget: show };
    await saveSettings(updatedSettings);
  };

  const contextValue = {
    userSettings,
    settings,
    settingsHaveLoaded,
    saveSettings,
    saveGridSettings,
    savePagingSettings,
    resetGridSettings,
    getColumnsWithSettings,
    saveColumnsToLocalStorage,
    saveShowOrderSummaryWidget,
  };

  return <SettingsContext.Provider value={contextValue}>{children}</SettingsContext.Provider>;
};

export default SettingsContext;
